import { useState, useEffect, useCallback, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import {
  X,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  Printer,
  Share2,
  Download,
  ChevronLeft,
  ChevronRight,
  RotateCw,
  FileText,
  Image as ImageIcon,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Button } from "./button";
import { toast } from "sonner";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * DocumentPreviewModal - Unified document preview with PDF.js and image viewer
 * 
 * Usage:
 * <DocumentPreviewModal
 *   open={showPreview}
 *   onClose={() => setShowPreview(false)}
 *   token={authToken}
 *   document={{
 *     document_id: "doc123",
 *     filename: "report.pdf",
 *     file_url: "/api/v1/attachments/doc123/download",
 *     content_type: "application/pdf",
 *     title: "Annual Report 2025"
 *   }}
 * />
 */

const DocumentPreviewModal = ({
  open,
  onClose,
  document: documentData,
  token,
}) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fitMode, setFitMode] = useState("width"); // "width", "page", "none"
  const [blobUrl, setBlobUrl] = useState(null);
  const containerRef = useRef(null);

  // Fetch file as blob with auth token
  useEffect(() => {
    if (!open || !documentData?.file_url) {
      setBlobUrl(null);
      return;
    }

    const fetchFile = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(documentData.file_url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (err) {
        console.error("Failed to fetch document:", err);
        setError("Failed to load document. Please try downloading instead.");
        setLoading(false);
      }
    };

    fetchFile();

    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [open, documentData?.file_url, token]);

  // Determine file type
  const getFileType = (doc) => {
    if (!doc) return "unknown";
    const contentType = doc.content_type?.toLowerCase() || "";
    const filename = doc.filename?.toLowerCase() || "";
    
    if (contentType.includes("pdf") || filename.endsWith(".pdf")) return "pdf";
    if (contentType.includes("image") || /\.(png|jpg|jpeg|gif|webp|svg)$/.test(filename)) return "image";
    if (contentType.includes("text") || /\.(txt|md|csv|json|xml)$/.test(filename)) return "text";
    return "unknown";
  };

  const fileType = getFileType(documentData);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      
      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "+":
        case "=":
          handleZoomIn();
          break;
        case "-":
          handleZoomOut();
          break;
        case "0":
          handleResetZoom();
          break;
        case "ArrowLeft":
          if (fileType === "pdf") goToPrevPage();
          break;
        case "ArrowRight":
          if (fileType === "pdf") goToNextPage();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, pageNumber, numPages]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // PDF handlers
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (err) => {
    console.error("PDF load error:", err);
    setLoading(false);
    setError("Failed to load document. Please try downloading instead.");
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages || 1));
  };

  // Zoom handlers
  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3.0));
    setFitMode("none");
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
    setFitMode("none");
  };

  const handleResetZoom = () => {
    setScale(1.0);
    setFitMode("width");
  };

  const handleFitWidth = () => {
    setFitMode("width");
    setScale(1.0);
  };

  const handleFitPage = () => {
    setFitMode("page");
    setScale(1.0);
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  // Print handler
  const handlePrint = () => {
    if (documentData?.file_url) {
      const printWindow = window.open(documentData.file_url, "_blank");
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    }
  };

  // Share handler
  const handleShare = async () => {
    const shareUrl = window.location.origin + documentData?.file_url;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: documentData?.title || documentData?.filename,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          copyToClipboard(shareUrl);
        }
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copied to clipboard");
  };

  // Download handler
  const handleDownload = () => {
    if (documentData?.file_url) {
      const link = window.document.createElement("a");
      link.href = documentData.file_url;
      link.download = documentData.filename || "document";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    }
  };

  // Click outside to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!open || !documentData) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={handleBackdropClick}
      data-testid="document-preview-modal"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal Container */}
      <div 
        className="relative w-[90vw] h-[90vh] max-w-6xl bg-background rounded-xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        ref={containerRef}
      >
        {/* Header / Toolbar */}
        <header className="flex items-center justify-between px-4 py-3 border-b bg-card">
          <div className="flex items-center gap-3">
            {fileType === "pdf" ? (
              <FileText className="w-5 h-5 text-red-500" />
            ) : fileType === "image" ? (
              <ImageIcon className="w-5 h-5 text-blue-500" />
            ) : (
              <FileText className="w-5 h-5 text-gray-500" />
            )}
            <div>
              <h3 className="font-semibold text-sm truncate max-w-[300px]">
                {documentData.title || documentData.filename}
              </h3>
              {fileType === "pdf" && numPages && (
                <p className="text-xs text-muted-foreground">
                  Page {pageNumber} of {numPages}
                </p>
              )}
            </div>
          </div>

          {/* Toolbar Actions */}
          <div className="flex items-center gap-1">
            {/* Zoom Controls */}
            <div className="flex items-center gap-1 mr-2 px-2 border-r">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleZoomOut}
                title="Zoom Out (-)"
                className="h-8 w-8"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-xs w-12 text-center">
                {Math.round(scale * 100)}%
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleZoomIn}
                title="Zoom In (+)"
                className="h-8 w-8"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
            </div>

            {/* Fit Controls */}
            <Button
              variant={fitMode === "width" ? "secondary" : "ghost"}
              size="icon"
              onClick={handleFitWidth}
              title="Fit to Width"
              className="h-8 w-8"
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
            <Button
              variant={fitMode === "page" ? "secondary" : "ghost"}
              size="icon"
              onClick={handleFitPage}
              title="Fit to Page"
              className="h-8 w-8"
            >
              <Minimize2 className="w-4 h-4" />
            </Button>

            {/* Rotate */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRotate}
              title="Rotate"
              className="h-8 w-8"
            >
              <RotateCw className="w-4 h-4" />
            </Button>

            <div className="w-px h-6 bg-border mx-1" />

            {/* Print */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrint}
              title="Print"
              className="h-8 w-8"
            >
              <Printer className="w-4 h-4" />
            </Button>

            {/* Share */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleShare}
              title="Share"
              className="h-8 w-8"
            >
              <Share2 className="w-4 h-4" />
            </Button>

            {/* Download */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDownload}
              title="Download"
              className="h-8 w-8"
            >
              <Download className="w-4 h-4" />
            </Button>

            <div className="w-px h-6 bg-border mx-1" />

            {/* Close */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              title="Close (ESC)"
              className="h-8 w-8"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </header>

        {/* Document Viewer */}
        <div className="flex-1 overflow-auto bg-muted/50 relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Loading document...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
              <div className="text-center max-w-md p-6">
                <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                <p className="text-sm text-muted-foreground mb-4">{error}</p>
                <Button onClick={handleDownload}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Instead
                </Button>
              </div>
            </div>
          )}

          {fileType === "pdf" && (
            <TransformWrapper
              initialScale={scale}
              minScale={0.5}
              maxScale={3}
              centerOnInit
              disabled={fitMode !== "none"}
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <TransformComponent
                  wrapperClass="!w-full !h-full"
                  contentClass="flex justify-center py-4"
                >
                  <Document
                    file={blobUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={null}
                    className="flex flex-col items-center gap-4"
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={scale}
                      rotate={rotation}
                      width={fitMode === "width" ? (containerRef.current?.clientWidth || 800) - 48 : undefined}
                      height={fitMode === "page" ? (containerRef.current?.clientHeight || 600) - 150 : undefined}
                      className="shadow-lg"
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </TransformComponent>
              )}
            </TransformWrapper>
          )}

          {fileType === "image" && (
            <TransformWrapper
              initialScale={1}
              minScale={0.5}
              maxScale={5}
              centerOnInit
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <TransformComponent
                  wrapperClass="!w-full !h-full"
                  contentClass="flex items-center justify-center p-4"
                >
                  <img
                    src={blobUrl || documentData.file_url}
                    alt={documentData.title || documentData.filename}
                    className="max-w-full max-h-full object-contain shadow-lg rounded"
                    style={{ transform: `rotate(${rotation}deg)` }}
                    onLoad={() => setLoading(false)}
                    onError={() => {
                      setLoading(false);
                      setError("Failed to load image");
                    }}
                  />
                </TransformComponent>
              )}
            </TransformWrapper>
          )}

          {fileType === "text" && (
            <div className="p-6">
              <iframe
                src={blobUrl || documentData.file_url}
                className="w-full h-full min-h-[500px] bg-white rounded border"
                title={documentData.title || documentData.filename}
                onLoad={() => setLoading(false)}
              />
            </div>
          )}

          {fileType === "unknown" && !loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center max-w-md p-6">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Preview not available</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  This file type cannot be previewed. Please download to view.
                </p>
                <Button onClick={handleDownload}>
                  <Download className="w-4 h-4 mr-2" />
                  Download File
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* PDF Page Navigation Footer */}
        {fileType === "pdf" && numPages && numPages > 1 && (
          <footer className="flex items-center justify-center gap-4 px-4 py-2 border-t bg-card">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={numPages}
                value={pageNumber}
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= numPages) {
                    setPageNumber(page);
                  }
                }}
                className="w-16 text-center border rounded px-2 py-1 text-sm"
              />
              <span className="text-sm text-muted-foreground">of {numPages}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </footer>
        )}
      </div>
    </div>
  );
};

export { DocumentPreviewModal };
